// uploadQueueWorkers.js - Individual file upload workers
import { createAsyncThunk } from "@reduxjs/toolkit";
import { uploadQueueAPI } from "./api";
import { calculateFileHash, generateFileChunks, MAX_RETRIES, startProcessing, cancelProcessing } from "./utils";

let isCancelled = false;
let hasMarkedCancelled = false;

export const cancelWorkerUpload = () => {
  isCancelled = true;
  cancelProcessing();
};

const markCancelled = async (accessCode, caseId, clipId, fileId) => {
  if (hasMarkedCancelled) return { error: "Upload already cancelled", fileId };
  hasMarkedCancelled = true;
  await uploadQueueAPI.cancel(accessCode, caseId, clipId);
  return {
    error: "Upload cancelled", fileId
  }
}

export const uploadFileWorker = createAsyncThunk(
  "uploadQueue/uploadFile",
  async ({ fileId }, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const { files, fileData, comment } = state.uploadQueue;
    const { accessCode } = state.auth;
    const { caseId, id: clipId } = state.clip.clip;
    if (isCancelled) {
      console.warn("[uploadFileWorker] Upload cancelled, exiting worker.");
      return rejectWithValue(await markCancelled(accessCode, caseId, clipId, fileId));
    }

    const file = files.find((f) => f.id === fileId);
    if (!file) {
      return rejectWithValue({ error: "File not found", fileId });
    }

    console.log(`[uploadFileWorker] Starting upload for file: ${file.name}`);

    try {
      dispatch(
        updateFileProgress({
          fileId,
          status: "hashing",
          percent: 0,
          message: "Calculating file hash...",
        })
      );

      const hashResult = await calculateFileHash(file.fileObject);
      if (!hashResult.success) {
        throw new Error(hashResult.error || "Failed to calculate file hash");
      }

      console.log(
        `[uploadFileWorker] Hash calculated for ${file.name}: ${hashResult.hash}`
      );

      const chunks = generateFileChunks(file);
      console.log(
        `[uploadFileWorker] Generated ${chunks.length} chunks for ${file.name}`
      );

      dispatch(
        updateFileProgress({
          fileId,
          status: "uploading",
          percent: 0,
          message: "Starting upload...",
          totalChunks: chunks.length,
        })
      );

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        let retryCount = 0;
        let chunkSuccess = false;

        while (!chunkSuccess && retryCount <= MAX_RETRIES) {
          if (isCancelled) {
            console.warn(
              "[uploadFileWorker] Upload cancelled, exiting worker."
            );
            return rejectWithValue(await markCancelled(accessCode, caseId, clipId, fileId));
          }
          try {
            // Update progress for current chunk
            dispatch(
              updateFileProgress({
                fileId,
                status: "uploading",
                percent: Math.round((i / chunks.length) * 100),
                message: `Uploading chunk ${i + 1} of ${chunks.length}${
                  retryCount > 0 ? ` (retry ${retryCount})` : ""
                }`,
                currentChunk: i + 1,
                totalChunks: chunks.length,
              })
            );

            const result = await uploadQueueAPI.uploadChunk(
              accessCode,
              caseId,
              clipId,
              chunk,
              file.fileObject,
              fileData,
              hashResult.hash,
              comment
            );

            if (result.success) {
              chunkSuccess = true;
              console.log(
                `[uploadFileWorker] Chunk ${i + 1}/${
                  chunks.length
                } uploaded successfully for ${file.name}`
              );

              dispatch(
                updateFileProgress({
                  fileId,
                  status: "uploading",
                  percent: (i / chunks.length) * 100,
                  message: `Uploading chunk ${i + 1} of ${chunks.length}`,
                  currentChunk: i + 1,
                  totalChunks: chunks.length,
                })
              );
              if (isCancelled) {
                console.warn("[uploadFileWorker] Upload cancelled, exiting worker.", caseId, clipId);
                return rejectWithValue(await markCancelled(accessCode, caseId, clipId, fileId));
              }
            } else {
              throw new Error(result.error || "Chunk upload failed");
            }
          } catch (error) {
            retryCount++;
            console.warn(
              `[uploadFileWorker] Chunk ${i + 1} failed for ${
                file.name
              }, retry ${retryCount}/${MAX_RETRIES}:`,
              error
            );

            if (retryCount > MAX_RETRIES) {
              throw new Error(
                `Chunk ${i + 1} failed after ${MAX_RETRIES} retries: ${
                  error.message
                }`
              );
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }

      dispatch(
        updateFileProgress({
          fileId,
          status: "complete",
          percent: 100,
          message: "Upload complete",
        })
      );

      console.log(
        `[uploadFileWorker] File upload completed successfully: ${file.name}`
      );
      return { fileId, success: true };
    } catch (error) {
      console.error(
        `[uploadFileWorker] Upload failed for ${file.name}:`,
        error
      );

      dispatch(
        updateFileProgress({
          fileId,
          status: "error",
          percent: 0,
          message: error.message || "Upload failed",
          error: error.message,
        })
      );

      return rejectWithValue({
        error: error.message || "Upload failed",
        fileId,
      });
    }
  }
);

export const updateFileProgress = (payload) => {
  if (isCancelled)
    return {
      type: "uploadQueue/reset",
    };
  return {
    type: "uploadQueue/updateFileProgress",
    payload,
  };
};

export const startUploadWorkers = createAsyncThunk(
  "uploadQueue/startWorkers",
  async (_, { getState, dispatch }) => {
    isCancelled = false;
    startProcessing();
    hasMarkedCancelled = false;
    const state = getState();
    const { files, config } = state.uploadQueue;

    console.log(
      `[startUploadWorkers] Starting upload for ${files.length} files with ${config.maxWorkers} workers`
    );

    const workerPromises = [];
    const maxWorkers = Math.min(config.maxWorkers, files.length);

    for (let i = 0; i < maxWorkers; i++) {
      if (i < files.length) {
        const file = files[i];
        const workerPromise = dispatch(uploadFileWorker({ fileId: file.id }));
        workerPromises.push(workerPromise);
      }
    }

    let nextFileIndex = maxWorkers;

    const startNextWorker = async () => {
      if (nextFileIndex < files.length) {
        const file = files[nextFileIndex];
        nextFileIndex++;

        console.log(
          `[startUploadWorkers] Starting worker for file ${nextFileIndex}/${files.length}: ${file.name}`
        );

        try {
          await dispatch(uploadFileWorker({ fileId: file.id }));
        } catch (error) {
          console.error(
            `[startUploadWorkers] Worker failed for ${file.name}:`,
            error
          );
        }

        startNextWorker();
      }
    };

    workerPromises.forEach((promise) => {
      promise.finally(() => {
        startNextWorker();
      });
    });

    return {
      totalFiles: files.length,
      maxWorkers,
      startedWorkers: maxWorkers,
    };
  }
);
